import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { translations } from '../../../../../locales/translations';
import { DataGrid } from '@mui/x-data-grid';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { apiList } from '../../../../../locales/apiList';
import { UseAxios } from '../../../../../utils/UseAxios';
import { useSelector } from 'react-redux';
import { selectContractNumber } from '../../slice/selectors';
import { toPathSegment } from '../../../../../utils/contractNumber';
import { commonStyle } from '../../../../../styles/theme/themes';
import { CircularProgress } from '@mui/material';
import moment from 'moment';
export function CorrespondingInvoiceTab() {
  const { t } = useTranslation();
  const contractNumber = useSelector(selectContractNumber);
  const [pageSize, setPageSize] = useState(10);
  const columns = useRef([
    {
      field: 'invoice_no',
      headerName: t(translations.invoiceNo),
      sortable: true,
      disableColumnMenu: true,
      width: 105,
    },
    {
      field: 'reference_nature',
      headerName: t(translations.refType),
      sortable: false,
      disableColumnMenu: true,
      width: 75,
    },
    {
      field: 'reference_no',
      headerName: t(translations.refNo),
      sortable: true,
      disableColumnMenu: true,
      flex: 3,
      minWidth: 150,
    },
    {
      field: 'customer_po_no',
      headerName: t(translations.custPO),
      sortable: true,
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'customer_code',
      headerName: t(translations.custCode),
      sortable: false,
      disableColumnMenu: true,
      width: 85,
    },
    {
      field: 'amount',
      headerName: t(translations.amount),
      sortable: true,
      disableColumnMenu: true,
      width: 140,
      renderCell: params => {
        return returnMoneyElement(params, 'amount');
      },
    },
    {
      field: 'remain_amount',
      headerName: t(translations.unpaidAmount),
      sortable: true,
      disableColumnMenu: true,
      width: 140,
      renderCell: params => {
        return returnMoneyElement(params, 'remain_amount');
      },
    },
    {
      field: 'payment_term',
      headerName: t(translations.paymentTerms),
      sortable: true,
      disableColumnMenu: true,
      width: 100,
    },
    {
      field: 'invoice_type',
      headerName: t(translations.invoiceType),
      sortable: true,
      disableColumnMenu: true,
      width: 100,
      renderCell: params => {
        return returnInvoiceTypeElement(params, 'invoice_type');
      },
    },
    {
      field: 'trx_date',
      headerName: t(translations.issueDate),
      sortable: true,
      disableColumnMenu: true,
      width: 100,
      renderCell: params => {
        return returnDateElement(params, 'trx_date');
      },
    },
    {
      field: 'amount_status',
      headerName: t(translations.paymentStatus),
      sortable: true,
      disableColumnMenu: true,
      width: 75,
      renderCell: params => {
        return returnAmountStatusElement(params, 'amount_status');
      },
    },
  ]);
  const [rows, setRows] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);

  React.useEffect(() => {
    UseAxios(`${apiList.getWorklistByIDURL}${toPathSegment(contractNumber)}`, 'get')
      .then(function(res) {
        const workList = res.data;
        const invoiceReference = contractNumber.replace('(Novated)', '').trim();
        const requestURL = `${apiList.geteBillInvoiceList}reference_number=${encodeURIComponent(invoiceReference)}`;
        UseAxios(requestURL, 'get', null, true)
          .then(function(response) {
            const invoices = response.data;
            if(invoices.length > 0){
              if(workList?.novatedDate && workList?.novatedDate !== "NA"){
                const firstCustomer = invoices
                  .sort((a, b) => moment(a.trx_date).unix() - moment(b.trx_date).unix())
                  .map((item) => item.customer_code)[0];

                if(contractNumber.includes('Novated')){
                  setRows(invoices.filter((item) => item.customer_code === firstCustomer));
                }else {
                  setRows(invoices.filter((item) => item.customer_code !== firstCustomer));
                }
              }else {
                setRows(invoices);
              }
            }else {
              setRows([]);
            }
            setLoading(false);
          })
          .catch(function(error) {
            console.log('error: ' + JSON.stringify(error));
          });

      })
      .catch(function (error) {
        console.log('error: ' + JSON.stringify(error));
      });
    ;

  }, [contractNumber]);

  return (
    <Wrapper>
      <div className='worklist-header'><FormatListBulletedIcon />{t(translations.invoiceList)}</div>
      {loading ?
        <CircularProgress />
        :
        <DataGrid
          rows={rows}
          getRowId={row => row.invoice_no}
          columns={columns.current}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 15, 20, 25, 30]}
          disableSelectionOnClick
          className='data-grid'
          autoHeight={true}
        />
      }
    </Wrapper>
  );
}

const returnDateElement = (params, columnName) => {
  let displayDate = params.row[columnName].split(' ');
  return (
    <div>
      {displayDate[0]}
    </div>
  );
};

const returnMoneyElement = (params, columnName) => {
  return (
    <div>
      {params.row.currency} {params.row[columnName].toLocaleString(undefined, { maximumFractionDigits: 2 })}
    </div>
  );
};

const returnAmountStatusElement = (params, columnName) => {
  return (
    <div className={`${params.row[columnName] === 'PAID' ? 'amount-status-paid' : 'amount-status-unpaid'} ${params.row[columnName] === 'PARTIAL' && 'amount-status-partial'}`}>
      {params.row[columnName]}
    </div>
  );
};

const returnInvoiceTypeElement = (params, columnName) => {
  let displayName = params.row[columnName];
  if (displayName === 'IA-RECURR') {
    displayName = 'RECURRING';
  } else if (displayName === 'IA-ONEOFF') {
    displayName = 'ONE-OFF';
  } else if (displayName === 'IA-DEPOSIT') {
    displayName = 'DEPOSIT';
  } else if (displayName === 'IA Credit') {
    displayName = 'CREDIT';
  }
  return (
    <div>
      {displayName}
    </div>
  );
};

const Wrapper = styled.div`
  width: 100%;
  box-shadow: ${commonStyle.boxShadow};
  border-radius: 5px;

  .worklist-header {
    display: flex;
    align-items: center;
    padding: 10px;
    border-radius: 5px;
    background-color: ${p => p.theme.primary};
    color: ${p => p.theme.background};
    font-size: 16px;
  }

  .MuiTableCell-root {
    line-height: 15px;
    border-right: 1px solid rgba(224, 224, 224, 1) !important;
  }

  .oacl-hyper {
    color: ${p => p.theme.buttonColor};
    text-decoration: underline;
  }

  .cell {
    display: flex;
    align-items: center;
  }

  .amount-status-paid {
    color: #53a965;
  }

  .amount-status-unpaid {
    color: red;
  }

  .amount-status-partial {
    color: #c1c32a;
  }

  .MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer {
    padding: 0;
  }

  .MuiDataGrid-root .MuiDataGrid-columnHeader,
  .MuiDataGrid-root .MuiDataGrid-cell {
    padding: 0 0 0 5px;
  }
`;
